import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Wallet3,
  Calculator,
  Logout,
  SidebarLeft,
  ProfileCircle,
  UserTick as UserTickIcon,
} from "iconsax-react";
import { useAuth } from "@/core/auth/hooks/useAuth";
import logoSrc from "@/assets/logo.svg";

/* ── Íconos SVG inline — vuesax/linear style (Figma) ─────── */

function HomeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9.02 2.84L3.63 7.04C2.73 7.74 2 9.23 2 10.36v7.41C2 19.92 3.53 22 5.52 22h13c1.99 0 3.48-2.08 3.48-4.23V10.5c0-1.18-.81-2.73-1.73-3.4L14.52 2.77c-1.31-.94-3.4-.9-4.5.07Z" />
      <path d="M12 18v-3" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <UserTickIcon className={className} variant="Linear" color="currentColor" />
  );
}

function ProfileIcon({ className }: { className?: string }) {
  return <ProfileCircle className={className} variant="Linear" color="currentColor" />;
}

function WalletIcon({ className }: { className?: string }) {
  return (
    <Wallet3 className={className} variant="Linear" color="currentColor" />
  );
}

function CalculatorIcon({ className }: { className?: string }) {
  return (
    <Calculator className={className} variant="Linear" color="currentColor" />
  );
}

function CallChatIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    >
      <path d="M17 12C19.7614 12 22 9.76142 22 7C22 4.23858 19.7614 2 17 2C14.2386 2 12 4.23858 12 7C12 7.79984 12.1878 8.55582 12.5217 9.22624C12.6105 9.4044 12.64 9.60803 12.5886 9.80031L12.2908 10.9133C12.1615 11.3965 12.6035 11.8385 13.0867 11.7092L14.1997 11.4114C14.392 11.36 14.5956 11.3895 14.7738 11.4783C15.4442 11.8122 16.2002 12 17 12Z" />
      <path d="M8.03759 7.31617L8.6866 8.4791C9.2723 9.52858 9.03718 10.9053 8.11471 11.8278C8.11471 11.8278 6.99578 12.9467 9.02451 14.9755C11.0532 17.0042 12.1722 15.8853 12.1722 15.8853C13.0947 14.9628 14.4714 14.7277 15.5209 15.3134L16.6838 15.9624C18.2686 16.8468 18.4557 19.0692 17.0628 20.4622C16.2258 21.2992 15.2004 21.9505 14.0669 21.9934C12.1588 22.0658 8.91828 21.5829 5.6677 18.3323C2.41713 15.0817 1.93421 11.8412 2.00655 9.93309C2.04952 8.7996 2.7008 7.77423 3.53781 6.93723C4.93076 5.54428 7.15317 5.73144 8.03759 7.31617Z" />
    </svg>
  );
}
function LogoutIcon({ className }: { className?: string }) {
  return <Logout className={className} variant="Linear" color="currentColor" />;
}

/* ── Navegación ───────────────────────────────────────────── */

export function Sidebar({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { logout } = useAuth();
  const { t } = useTranslation("common");

  const NAV_ITEMS = [
    { to: "/dashboard", label: t("nav.home"), Icon: HomeIcon },
    { to: "/productos", label: t("nav.products"), Icon: WalletIcon },
    { to: "/perfilamiento", label: t("nav.profiling"), Icon: UserIcon },
    { to: "/simulador", label: t("nav.simulator"), Icon: CalculatorIcon },
    { to: "/contacto", label: t("nav.contact"), Icon: CallChatIcon },
  ];

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 md:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={[
          "fixed inset-y-4 left-4 z-30 flex w-[min(13.75rem,calc(100vw-2rem))] flex-col overflow-hidden",
          "rounded-sm bg-[#05141f] shadow-lg transition-transform duration-200",
          isOpen ? "translate-x-0" : "-translate-x-[120%]",
          "md:sticky md:top-4 md:z-10 md:m-[1rem_0_1rem_1rem] md:h-[calc(100svh-2rem)] md:w-[clamp(11rem,14vw,13.75rem)] md:translate-x-0",
        ].join(" ")}
      >
        <div className="flex justify-end px-4 pt-4 md:hidden">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md p-1.5 text-white/70 transition-colors duration-150 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
            aria-label="Cerrar menú"
            onClick={onClose}
          >
            <SidebarLeft size={22} variant="Linear" color="currentColor" />
          </button>
        </div>

        <div className="flex items-center justify-center px-3 pb-11 pt-8 md:pb-11 md:pt-11">
          <img
            src={logoSrc}
            alt="SynerGy"
            className="block h-auto w-full max-w-48"
          />
        </div>

        <nav
          className="flex flex-col gap-3 overflow-y-auto px-[1.125rem]"
          aria-label={t("nav.home")}
        >
          {NAV_ITEMS.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/dashboard"}
              className={({ isActive }) =>
                [
                  "flex h-11 w-full items-center gap-2.5 rounded-sm px-3.5 text-left text-sm font-normal",
                  "text-[#9ebbd6] transition-colors duration-150 hover:bg-[rgba(0,61,102,0.5)] hover:text-white",
                  isActive ? "bg-[#003d66] text-white" : "bg-transparent",
                ].join(" ")
              }
              onClick={onClose}
            >
              <Icon className="h-5 w-5 shrink-0 text-white" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto flex flex-col px-[1.125rem] pb-28">
          <NavLink
            to="/perfil"
            className={({ isActive }) =>
              [
                "flex h-11 w-full items-center gap-2.5 rounded-sm px-3.5 text-left text-sm font-normal",
                "text-[#9ebbd6] transition-colors duration-150 hover:bg-[rgba(0,61,102,0.5)] hover:text-white",
                isActive ? "bg-[#003d66] text-white" : "bg-transparent",
              ].join(" ")
            }
            onClick={onClose}
          >
            <ProfileIcon className="h-5 w-5 shrink-0 text-white" />
            {t("nav.profile")}
          </NavLink>
          <button
            type="button"
            className="flex h-11 w-full items-center gap-2.5 rounded-sm px-3.5 text-left text-sm font-normal text-[#9ebbd6] transition-colors duration-150 hover:bg-[rgba(0,61,102,0.5)] hover:text-white"
            onClick={() => {
              onClose();
              void logout();
            }}
          >
            <LogoutIcon className="h-5 w-5 shrink-0 text-white" />
            {t("nav.logout")}
          </button>
        </div>
      </aside>
    </>
  );
}
